import React from 'react';

/** 카메라 트랙의 초점 조절 기능(Chrome 등 Image Capture 확장 제약 지원 브라우저) */
type FocusCaps = {
  canManual: boolean;
  canAuto: boolean;
  min: number;
  max: number;
  step: number;
};

type FocusCapabilities = MediaTrackCapabilities & {
  focusMode?: string[];
  focusDistance?: { min?: number; max?: number; step?: number };
};
type FocusSettings = MediaTrackSettings & { focusMode?: string; focusDistance?: number };

const UNSUPPORTED_TIP = '이 카메라는 초점 조절을 지원하지 않아요';

export function readWebcamFocusCaps(track: MediaStreamTrack | null): FocusCaps | null {
  if (!track || track.readyState !== 'live' || typeof track.getCapabilities !== 'function') return null;
  let caps: FocusCapabilities;
  try {
    caps = track.getCapabilities() as FocusCapabilities;
  } catch {
    return null;
  }
  const modes = Array.isArray(caps.focusMode) ? caps.focusMode : [];
  const fd = caps.focusDistance;
  const min = typeof fd?.min === 'number' ? fd.min : NaN;
  const max = typeof fd?.max === 'number' ? fd.max : NaN;
  const canManual = modes.includes('manual') && Number.isFinite(min) && Number.isFinite(max) && max > min;
  if (!canManual) return null;
  const step = typeof fd?.step === 'number' && fd.step > 0 ? fd.step : (max - min) / 100;
  return { canManual, canAuto: modes.includes('continuous'), min, max, step };
}

async function applyFocus(track: MediaStreamTrack, c: Record<string, unknown>): Promise<boolean> {
  try {
    await track.applyConstraints({ advanced: [c as MediaTrackConstraintSet] });
    return true;
  } catch {
    return false;
  }
}

type Props = {
  getVideoTrack: () => MediaStreamTrack | null;
  onBeforeOpen?: () => void;
};

/** 웹캠 그리기 툴바 안의 초점 버튼 + 자동/수동·슬라이더 팝오버 (수업 프레젠·메인 프레젠 공용) */
export const WebcamFocusControl: React.FC<Props> = ({ getVideoTrack, onBeforeOpen }) => {
  const [caps, setCaps] = React.useState<FocusCaps | null>(null);
  const [open, setOpen] = React.useState(false);
  const [mode, setMode] = React.useState<'auto' | 'manual'>('auto');
  const [distance, setDistance] = React.useState(0);
  const [error, setError] = React.useState('');
  const trackRef = React.useRef<MediaStreamTrack | null>(null);
  const wrapRef = React.useRef<HTMLDivElement | null>(null);

  const refresh = React.useCallback(() => {
    const track = getVideoTrack();
    const next = readWebcamFocusCaps(track);
    if (track !== trackRef.current) {
      trackRef.current = track;
      setMode('auto');
      setError('');
      if (!track) setOpen(false);
      if (track && next) {
        const s = (track.getSettings?.() ?? {}) as FocusSettings;
        setMode(s.focusMode === 'manual' ? 'manual' : 'auto');
        setDistance(typeof s.focusDistance === 'number' ? s.focusDistance : (next.min + next.max) / 2);
      }
    }
    setCaps((prev) =>
      prev && next && prev.min === next.min && prev.max === next.max && prev.step === next.step && prev.canAuto === next.canAuto
        ? prev
        : next
    );
  }, [getVideoTrack]);

  React.useEffect(() => {
    refresh();
    const id = window.setInterval(refresh, 1500);
    return () => window.clearInterval(id);
  }, [refresh]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        setOpen(false);
      }
    };
    const onDocPointerDown = (ev: PointerEvent) => {
      const root = wrapRef.current;
      if (!root || root.contains(ev.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', onDocPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('pointerdown', onDocPointerDown, true);
    };
  }, [open]);

  const setManual = async (value: number) => {
    const track = trackRef.current;
    if (!track) return;
    setDistance(value);
    setMode('manual');
    const ok = await applyFocus(track, { focusMode: 'manual', focusDistance: value });
    setError(ok ? '' : '초점을 바꾸지 못했어요');
  };

  const setAuto = async () => {
    const track = trackRef.current;
    if (!track) return;
    setMode('auto');
    const ok = await applyFocus(track, { focusMode: 'continuous' });
    setError(ok ? '' : '자동 초점으로 되돌리지 못했어요');
  };

  const supported = !!caps;
  const pct = caps ? Math.round(((distance - caps.min) / (caps.max - caps.min)) * 100) : 0;

  return (
    <div className="cp-program-slide-webcam-draw-color-wrap cp-program-slide-webcam-draw-focus-wrap" ref={wrapRef}>
      <button
        type="button"
        className={
          'cp-program-slide-webcam-draw-tool-btn' +
          (open || (supported && mode === 'manual') ? ' is-active' : '') +
          (supported ? '' : ' is-unsupported')
        }
        title={supported ? (mode === 'manual' ? '웹캠 초점 · 수동' : '웹캠 초점 · 자동') : UNSUPPORTED_TIP}
        aria-label={supported ? '웹캠 초점 조절' : `웹캠 초점 조절 (${UNSUPPORTED_TIP})`}
        aria-expanded={open}
        aria-disabled={!supported || undefined}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          if (!supported) refresh();
          onBeforeOpen?.();
          setOpen((v) => !v);
        }}
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden>
          <path
            d="M4 8V5.5A1.5 1.5 0 0 1 5.5 4H8M16 4h2.5A1.5 1.5 0 0 1 20 5.5V8M20 16v2.5a1.5 1.5 0 0 1-1.5 1.5H16M8 20H5.5A1.5 1.5 0 0 1 4 18.5V16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          />
          <circle cx="12" cy="12" r="3.2" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
        {supported && mode === 'manual' ? <span className="cp-program-slide-webcam-draw-focus-dot" aria-hidden /> : null}
      </button>
      {open && !caps ? (
        <div
          className="cp-program-slide-webcam-draw-color-menu cp-program-slide-webcam-draw-focus-menu"
          role="dialog"
          aria-label="웹캠 초점"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <span className="cp-program-slide-webcam-draw-focus-title">초점</span>
          <p className="cp-program-slide-webcam-draw-focus-hint">
            {trackRef.current ? UNSUPPORTED_TIP : '웹캠이 켜져 있을 때 초점을 조절할 수 있어요'}
            {trackRef.current ? ' (Chrome·Edge + 초점 조절이 되는 USB 웹캠에서 사용할 수 있어요)' : ''}
          </p>
        </div>
      ) : null}
      {open && caps ? (
        <div
          className="cp-program-slide-webcam-draw-color-menu cp-program-slide-webcam-draw-focus-menu"
          role="dialog"
          aria-label="웹캠 초점"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div className="cp-program-slide-webcam-draw-focus-head">
            <span className="cp-program-slide-webcam-draw-focus-title">초점</span>
            <div className="cp-program-slide-webcam-draw-focus-seg" role="radiogroup" aria-label="초점 방식">
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'auto'}
                className={mode === 'auto' ? 'is-on' : ''}
                disabled={!caps.canAuto}
                title={caps.canAuto ? '자동 초점' : '이 카메라는 자동 초점으로 되돌릴 수 없어요'}
                onClick={() => void setAuto()}
              >
                자동
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={mode === 'manual'}
                className={mode === 'manual' ? 'is-on' : ''}
                onClick={() => void setManual(distance)}
              >
                수동
              </button>
            </div>
          </div>
          <div className={'cp-program-slide-webcam-draw-focus-slider' + (mode === 'manual' ? '' : ' is-idle')}>
            <span aria-hidden>가까이</span>
            <input
              type="range"
              min={caps.min}
              max={caps.max}
              step={caps.step}
              value={Math.min(caps.max, Math.max(caps.min, distance))}
              aria-label="초점 거리"
              aria-valuetext={`${pct}%`}
              onChange={(e) => void setManual(Number(e.target.value))}
            />
            <span aria-hidden>멀리</span>
          </div>
          <p className="cp-program-slide-webcam-draw-focus-hint">
            {error || (mode === 'manual' ? '막대를 움직여 글씨가 또렷해지는 곳을 찾으세요.' : '카메라가 알아서 초점을 맞춰요.')}
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default WebcamFocusControl;
